import {
  formatLocalDateTime,
  formatNumber,
  parseUtcInstant,
} from "./lidarTelemetryFields";

function parseNestedMpptFields(row) {
  const mppt =
    row?.mppt ?? row?.payload?.mppt ?? row?.payload?.data?.mppt ?? null;

  if (!mppt || typeof mppt !== "object") {
    return {};
  }

  // Flat firmware fields + legacy nested panel_data / battery_data / status.
  const panel = mppt.panel_data ?? mppt.panelData ?? {};
  const battery = mppt.battery_data ?? mppt.batteryData ?? {};
  const status = mppt.status ?? {};

  return {
    mpptPanelVoltage: mppt.panel_voltage ?? mppt.panelVoltage ?? panel.voltage ?? null,
    mpptPanelCurrent: mppt.panel_current ?? mppt.panelCurrent ?? panel.current ?? null,
    mpptPanelPower: mppt.panel_power ?? mppt.panelPower ?? panel.power ?? null,
    mpptBatteryVoltage:
      mppt.battery_voltage ?? mppt.batteryVoltage ?? battery.voltage ?? null,
    mpptBatteryCurrent:
      mppt.battery_current ?? mppt.batteryCurrent ?? battery.current ?? null,
    mpptBatteryPower:
      mppt.battery_power ?? mppt.batteryPower ?? battery.power ?? null,
    mpptBatteryPercentage:
      mppt.battery_percent ??
      mppt.batteryPercent ??
      mppt.battery_percentage ??
      mppt.batteryPercentage ??
      battery.percentage ??
      battery.percent ??
      null,
    mpptChargingStatus:
      mppt.charging_status ??
      mppt.chargingStatus ??
      status.charging_status ??
      status.chargingStatus ??
      null,
    mpptFaultCode:
      mppt.fault_code ??
      mppt.faultCode ??
      status.fault_code ??
      status.faultCode ??
      null,
  };
}

function parseNestedRainfallFields(row) {
  const rainfall =
    row?.rainfall ?? row?.payload?.rainfall ?? row?.payload?.data?.rainfall ?? null;

  if (!rainfall || typeof rainfall !== "object") {
    return {};
  }

  return {
    rainfallIntensityMmPerMin:
      rainfall.intensity_mm_per_min ??
      rainfall.intensityMmPerMin ??
      rainfall.intensity ??
      null,
    rainfallCumulativeMm:
      rainfall.cumulative_mm ??
      rainfall.cumulativeMm ??
      rainfall.cumulative ??
      null,
  };
}

function parseNestedLidarFields(row) {
  const lidar =
    row?.lidar ??
    row?.payload?.lidar ??
    row?.payload?.data?.lidar ??
    null;

  if (!lidar || typeof lidar !== "object") {
    return {};
  }

  const confirmedDistAvgCm =
    lidar.confirmed_dist_avg_cm ??
    lidar.confirmedDistAvgCm ??
    null;
  const waterLevelAvgCm =
    lidar.water_level_avg_cm ??
    lidar.waterLevelAvgCm ??
    null;
  const confirmedLevelAvgCm =
    lidar.confirmed_level_avg_cm ?? lidar.confirmedLevelAvgCm ?? null;
  const rawDistanceCm =
    lidar.raw_distance_cm ?? lidar.rawDistanceCm ?? null;

  return {
    confirmedDistAvgCm,
    waterLevelAvgCm,
    confirmedLevelAvgCm,
    rawDistanceCm,
    strength: lidar.strength ?? null,
    confidencePct: lidar.confidence_pct ?? lidar.confidencePct ?? null,
    referenceHeightCm:
      lidar.reference_height_cm ?? lidar.referenceHeightCm ?? null,
    dataValid: lidar.data_valid ?? lidar.dataValid ?? null,
    lidarDistanceCm: lidar.distance_cm ?? lidar.distanceCm ?? null,
    lidarLevelCm: lidar.level_cm ?? lidar.levelCm ?? null,
  };
}

/** Map legacy Redis/socket keys (panelVoltage) to current mppt_* fields. */
function normalizeSensorLivePayload(row) {
  if (!row || typeof row !== "object") return {};

  const nestedLidar = parseNestedLidarFields(row);
  const nestedMppt = parseNestedMpptFields(row);
  const nestedRainfall = parseNestedRainfallFields(row);

  return {
    ...row,
    deviceRole: row.deviceRole ?? row.device_role ?? null,
    receivedAt:
      row.receivedAt ?? row.timestamp ?? row.payload?.timestamp ?? null,
    lidarDistanceCm:
      row.lidarDistanceCm ??
      row.lidar_distance_cm ??
      nestedLidar.lidarDistanceCm ??
      null,
    lidarLevelCm:
      row.lidarLevelCm ?? row.lidar_level_cm ?? nestedLidar.lidarLevelCm ?? null,
    confirmedDistAvgCm:
      row.confirmedDistAvgCm ??
      row.confirmed_dist_avg_cm ??
      nestedLidar.confirmedDistAvgCm ??
      null,
    waterLevelAvgCm:
      row.waterLevelAvgCm ??
      row.water_level_avg_cm ??
      nestedLidar.waterLevelAvgCm ??
      null,
    confirmedLevelAvgCm:
      row.confirmedLevelAvgCm ??
      row.confirmed_level_avg_cm ??
      nestedLidar.confirmedLevelAvgCm ??
      null,
    rawDistanceCm:
      row.rawDistanceCm ??
      row.raw_distance_cm ??
      nestedLidar.rawDistanceCm ??
      null,
    strength: row.strength ?? nestedLidar.strength ?? null,
    confidencePct:
      row.confidencePct ??
      row.confidence_pct ??
      nestedLidar.confidencePct ??
      null,
    referenceHeightCm:
      row.referenceHeightCm ??
      row.reference_height_cm ??
      nestedLidar.referenceHeightCm ??
      null,
    dataValid:
      row.dataValid ?? row.data_valid ?? nestedLidar.dataValid ?? null,
    rainfallIntensityMmPerMin:
      row.rainfallIntensityMmPerMin ??
      row.rainfall_intensity_mm_per_min ??
      nestedRainfall.rainfallIntensityMmPerMin ??
      null,
    rainfallCumulativeMm:
      row.rainfallCumulativeMm ??
      row.rainfall_cumulative_mm ??
      nestedRainfall.rainfallCumulativeMm ??
      null,
    mpptPanelVoltage:
      row.mpptPanelVoltage ??
      row.panelVoltage ??
      row.mppt_panel_voltage ??
      nestedMppt.mpptPanelVoltage ??
      null,
    mpptPanelCurrent:
      row.mpptPanelCurrent ??
      row.panelCurrent ??
      row.mppt_panel_current ??
      nestedMppt.mpptPanelCurrent ??
      null,
    mpptPanelPower:
      row.mpptPanelPower ??
      row.panelPower ??
      row.mppt_panel_power ??
      nestedMppt.mpptPanelPower ??
      null,
    mpptBatteryVoltage:
      row.mpptBatteryVoltage ??
      row.batteryVoltage ??
      row.mppt_battery_voltage ??
      nestedMppt.mpptBatteryVoltage ??
      null,
    mpptBatteryCurrent:
      row.mpptBatteryCurrent ??
      row.batteryCurrent ??
      row.mppt_battery_current ??
      nestedMppt.mpptBatteryCurrent ??
      null,
    mpptBatteryPower:
      row.mpptBatteryPower ??
      row.batteryPower ??
      row.mppt_battery_power ??
      nestedMppt.mpptBatteryPower ??
      null,
    mpptBatteryPercentage:
      row.mpptBatteryPercentage ??
      row.batteryPercentage ??
      row.mppt_battery_percentage ??
      nestedMppt.mpptBatteryPercentage ??
      null,
    mpptChargingStatus:
      row.mpptChargingStatus ??
      row.chargingStatus ??
      row.mppt_charging_status ??
      nestedMppt.mpptChargingStatus ??
      null,
    mpptFaultCode:
      row.mpptFaultCode ??
      row.faultCode ??
      row.mppt_fault_code ??
      nestedMppt.mpptFaultCode ??
      null,
  };
}

export function resolveSensorTelemetryDisplay(row) {
  const normalized = normalizeSensorLivePayload(row);

  return {
    deviceId: normalized.deviceId,
    deviceRole: normalized.deviceRole,
    topic: normalized.topic,
    receivedAt: normalized.receivedAt,
    lidarDistanceCm: normalized.lidarDistanceCm,
    lidarLevelCm: normalized.lidarLevelCm,
    confirmedDistAvgCm: normalized.confirmedDistAvgCm,
    waterLevelAvgCm: normalized.waterLevelAvgCm,
    confirmedLevelAvgCm: normalized.confirmedLevelAvgCm,
    rawDistanceCm: normalized.rawDistanceCm,
    strength: normalized.strength,
    confidencePct: normalized.confidencePct,
    referenceHeightCm: normalized.referenceHeightCm,
    dataValid: normalized.dataValid,
    rainfallIntensityMmPerMin: normalized.rainfallIntensityMmPerMin,
    rainfallCumulativeMm: normalized.rainfallCumulativeMm,
    mpptPanelVoltage: normalized.mpptPanelVoltage,
    mpptPanelCurrent: normalized.mpptPanelCurrent,
    mpptPanelPower: normalized.mpptPanelPower,
    mpptBatteryVoltage: normalized.mpptBatteryVoltage,
    mpptBatteryCurrent: normalized.mpptBatteryCurrent,
    mpptBatteryPower: normalized.mpptBatteryPower,
    mpptBatteryPercentage: normalized.mpptBatteryPercentage,
    mpptLoadVoltage: normalized.mpptLoadVoltage,
    mpptLoadCurrent: normalized.mpptLoadCurrent,
    mpptLoadPower: normalized.mpptLoadPower,
    mpptLoadStatus: normalized.mpptLoadStatus,
    mpptLoadBrightness: normalized.mpptLoadBrightness,
    mpptChargingStatus: normalized.mpptChargingStatus,
    mpptFaultCode: normalized.mpptFaultCode,
  };
}

export function sensorTelemetryRecordKey(row) {
  const display = resolveSensorTelemetryDisplay(row);
  if (row?.id) return String(row.id);
  return `${display.deviceId}|${display.receivedAt ?? ""}`;
}

/** Match live + API rows for the same reading (UUID differs between socket and DB). */
export function sensorTelemetryDedupeKey(row) {
  const display = resolveSensorTelemetryDisplay(row);
  const instant = parseUtcInstant(display.receivedAt);
  if (!display.deviceId || !instant) return sensorTelemetryRecordKey(row);
  return `${display.deviceId}|${instant.utc().format("YYYY-MM-DDTHH:mm")}`;
}

function toTelemetryNumber(value) {
  if (value === 0 || value === "0") return 0;
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

/** Cumulative rainfall — `rainfall_cumulative_mm` only. */
export function getRainfallCumulativeMm(row) {
  const display =
    row?.rainfallCumulativeMm !== undefined
      ? row
      : resolveSensorTelemetryDisplay(row);
  return toTelemetryNumber(display.rainfallCumulativeMm);
}

/** Rain intensity — `rainfall_intensity_mm_per_min` only. */
export function getRainfallIntensityMmPerMin(row) {
  const display =
    row?.rainfallIntensityMmPerMin !== undefined
      ? row
      : resolveSensorTelemetryDisplay(row);
  return toTelemetryNumber(display.rainfallIntensityMmPerMin);
}

export const RAIN_ACTIVE_INTENSITY_MM_PER_MIN = 0;

function pickFirstReadingValue(values) {
  for (const value of values) {
    if (value === 0 || value === "0") return Number(value);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

const LATEST_READING_META_KEYS = new Set([
  "deviceId",
  "deviceRole",
  "topic",
  "receivedAt",
]);

/** Newest row with gaps filled from recent readings (socket rows can be sparse). */
export function mergeLatestSensorReadings(rows) {
  if (!rows?.length) return null;

  const sorted = [...rows].sort(
    (a, b) =>
      (parseUtcInstant(b.receivedAt)?.valueOf() ?? 0) -
      (parseUtcInstant(a.receivedAt)?.valueOf() ?? 0),
  );

  const displays = sorted.map((row) => resolveSensorTelemetryDisplay(row));
  const merged = { ...displays[0] };

  for (const key of Object.keys(merged)) {
    if (LATEST_READING_META_KEYS.has(key)) continue;
    merged[key] = pickFirstReadingValue(displays.map((display) => display[key]));
  }

  return merged;
}

export function formatDataValid(value) {
  if (value === true || value === "true" || value === 1 || value === "1") {
    return { valid: true };
  }
  if (value === false || value === "false" || value === 0 || value === "0") {
    return { valid: false };
  }
  return { valid: null };
}

export { formatLocalDateTime, formatNumber };
